import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Netgear.css";
import netgearLogo from "../../assets/images/partners/Netgear.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const productCards = [
  {
    cat: "Switching", wifi7: false,
    name: "M4300 Series — Fully Managed Layer 3",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=700&q=80",
    desc: "Enterprise-class stackable Layer 3 switches with CLI, SNMP, and REST API management. Up to 64-port stacks with 10G/40G uplinks, 802.3bt PoE++, and redundant power — delivering full-featured enterprise switching at significantly lower cost than traditional enterprise vendors.",
    specs: ["Up to 64-port stacks", "10G / 40G uplinks", "Full L3 routing", "802.3bt PoE++ 90W/port", "Redundant PSU"],
  },
  {
    cat: "Wireless", wifi7: true,
    name: "WAX630E — Wi-Fi 6E Tri-Band AP",
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=700&q=80",
    desc: "Tri-band Wi-Fi 6E access point delivering 6 GHz spectrum access for uncongested high-density environments. Cloud-managed via Netgear Insight or on-prem via WC series controllers — supporting up to 300 concurrent clients per AP with WPA3 enterprise security.",
    specs: ["Wi-Fi 6E (6GHz)", "Tri-band OFDMA", "Up to 300 clients", "2.5G uplink", "WPA3 Enterprise"],
  },
  {
    cat: "Switching", wifi7: false,
    name: "ProSAFE GS748T — Smart Managed",
    img: "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=700&q=80",
    desc: "Smart managed Layer 2+ switching for organizations needing VLAN, QoS, and link aggregation without full CLI management complexity. Web GUI management with intuitive VLAN configuration — ideal for SMBs deploying their first managed switching environment.",
    specs: ["48-port Gigabit", "PoE/PoE+ available", "802.1Q VLANs", "LACP bonding", "Web management"],
  },
];

const wifi7Advantages = [
  { val: "4× Faster", label: "Than Wi-Fi 5" },
  { val: "6 GHz", label: "New Spectrum Band" },
  { val: "320 MHz", label: "Max Channel Width" },
  { val: "Multi-Link", label: "Operation (MLO)" },
];

const switchItems = [
  { icon: "🔧", name: "ProSAFE Plus — Unmanaged+", desc: "Entry-level smart switches for small offices — basic VLAN and QoS configuration through a browser-based interface without requiring network engineering expertise." },
  { icon: "⚙️", name: "ProSAFE Smart Managed", desc: "Layer 2+ smart managed switches (GS308T, GS748T series) with VLAN, QoS, LACP, and IGMP snooping — managed via web browser or SNMP without requiring CLI expertise." },
  { icon: "🖥️", name: "M4250 — AV Line Smart Managed Pro", desc: "AV-optimized managed switches with IGMP snooping and PTP/IEEE 1588 time synchronization — purpose-built for AV over IP environments using Dante, NDI, and AVB protocols." },
  { icon: "🏢", name: "M4300 — Fully Managed Enterprise", desc: "Enterprise-class L3 stackable switches with full CLI, SNMP v3, REST API, and RFC-compliant routing — delivering enterprise switching functionality at a fraction of traditional vendor cost." },
  { icon: "⚡", name: "M4500 — Multi-Gig High Density", desc: "Multi-gigabit access switching with 2.5G, 5G, and 10G ports for high-density wireless deployments powering Wi-Fi 6E access points requiring multi-gig backbone connectivity." },
];

const solutions = [
  { icon: "📡", title: "Enterprise Campus Wireless", desc: "Netgear WAX series Wi-Fi 6E APs deployed in high-density campus environments — managed via Insight cloud or on-premises WC series controllers with centralized RF management and rogue AP detection." },
  { icon: "🏢", title: "SMB Office Networking", desc: "Complete SMB networking stack — Orbi Pro mesh Wi-Fi 6, ProSAFE smart switches, and Netgear Insight management — delivering enterprise reliability with simplified administration for organizations without dedicated IT staff." },
  { icon: "🎬", title: "AV over IP Production", desc: "ProAV switching with IGMP snooping, PTP time synchronization, and AV VLAN automation — supporting Dante audio, NDI video, and HDMI-over-IP production environments with AV-aware network management." },
  { icon: "🔒", title: "Secure Branch Networking", desc: "Netgear Insight Managed switches and APs with 802.1X port authentication, RADIUS integration, and per-port security policies — securing branch office network access without requiring enterprise infrastructure." },
  { icon: "📊", title: "Insight Cloud Management", desc: "Netgear Insight provides centralized cloud management for all Netgear switches and APs — enabling remote configuration, monitoring, firmware updates, and alerting from any device, anywhere." },
  { icon: "🔌", title: "High-Power PoE Deployments", desc: "ProSAFE and M4500 series switches with 802.3bt PoE++ delivering up to 90W per port — powering Pan-Tilt-Zoom cameras, digital signage, video conferencing endpoints, and Wi-Fi 6E access points." },
];

const remoteChecklist = [
  { icon: "📱", title: "Insight Mobile App Management", desc: "Manage your complete Netgear network from your smartphone — view device status, configure VLANs, troubleshoot connectivity, and receive alert notifications without opening a browser." },
  { icon: "🌐", title: "Orbi Pro for Remote Offices", desc: "Orbi Pro Wi-Fi 6 mesh systems deliver enterprise-class wireless coverage for home offices and small satellite locations — centrally managed through Insight alongside primary office infrastructure." },
  { icon: "🔐", title: "WPA3 Enterprise Security", desc: "All Netgear business Wi-Fi 6 and 6E access points support WPA3 Enterprise with 802.1X RADIUS authentication — ensuring remote workers connect with the same security standards as the corporate office." },
  { icon: "📊", title: "Insight Analytics & Reporting", desc: "Insight provides client connectivity history, device health trending, and bandwidth utilization reporting across all locations — enabling proactive identification of network performance issues." },
];

const deploySteps = [
  { num: "1", title: "Network Assessment", desc: "Evaluate current infrastructure, user density requirements, coverage gaps, and PoE power requirements to design the right Netgear solution for your environment." },
  { num: "2", title: "Solution Design", desc: "Netgear switching and wireless architecture designed for your building layout, VLAN requirements, and management preferences — Insight cloud or on-premises controller." },
  { num: "3", title: "Configuration & Staging", desc: "All switches and APs pre-configured in ALG Data Guard's staging environment before delivery — VLANs, QoS, security policies, and Insight enrollment completed before on-site installation." },
  { num: "4", title: "Deployment & Validation", desc: "On-site installation, cable management, PoE device commissioning, and wireless coverage verification — ensuring every device performs to specification before project sign-off." },
];

const smbPanels = [
  {
    tier: "Insight Managed",
    title: "Smart Managed for SMB",
    sub: "Cloud-managed business networking with app-based management — ideal for 10-100 user organizations with limited IT staff.",
    features: [
      "Netgear Insight mobile app and cloud portal",
      "ProSAFE smart managed switches — 8 to 48 ports",
      "Orbi Pro or WAX series Wi-Fi 6/6E APs",
      "Automatic firmware updates from Insight",
      "Email and SMS alerts for network events",
      "Basic traffic analytics and client reporting",
      "ALG Data Guard Insight setup and monitoring",
    ],
  },
  {
    tier: "Pro Managed",
    title: "Fully Managed Enterprise",
    sub: "Full-featured enterprise networking with CLI management — for organizations needing advanced routing, high-availability, and complete control.",
    features: [
      "M4300/M4500 fully managed L3 stackable switches",
      "WC series on-premises wireless controller",
      "OSPF, BGP, and VRRP for network redundancy",
      "802.1X port security with RADIUS integration",
      "SNMPv3 and REST API for NOC integration",
      "Multi-gig backbone for Wi-Fi 6E AP density",
      "ALG Data Guard full managed network operations",
    ],
  },
];

const certCards = [
  { icon: "✅", name: "Netgear Authorized Reseller", desc: "Direct Netgear reseller providing official pricing, warranty registration, and manufacturer support escalation for all Netgear business products." },
  { icon: "🔧", name: "Netgear Certified Technicians", desc: "ALG Data Guard engineers are trained on the complete Netgear business portfolio — including M4300 CLI configuration, Insight deployment, and ProAV network design." },
  { icon: "🤝", name: "Partner Support Access", desc: "Netgear partner support access for pre-sales engineering, technical escalations, and product availability — ensuring timely resolution for complex deployments." },
  { icon: "📦", name: "Authorized Service Provider", desc: "ALG Data Guard provides warranty claim processing, advance replacement coordination, and post-warranty repair services for Netgear business hardware." },
];

const faqData = [
  { q: "What is Netgear Insight and how does it simplify SMB network management?", a: "Netgear Insight is a cloud-based network management platform that consolidates administration for Netgear switches and Wi-Fi access points into a single app and web portal — accessible from any smartphone, tablet, or computer. For SMBs without dedicated network administrators, Insight dramatically simplifies network management: you can configure VLANs, set up wireless SSIDs, create port profiles, and monitor device health without opening a CLI. Insight's auto-discovery automatically detects newly connected Netgear devices and applies network profiles — meaning an IT generalist can deploy and manage enterprise-grade switching and wireless infrastructure that would previously require a network specialist. Key Insight capabilities: remote device management from anywhere via the mobile app, automatic firmware update scheduling, email and push notification alerts for device offline events, basic traffic analytics, and configuration backup and restore. ALG Data Guard sets up Insight organizations for customers — configuring the management hierarchy, device onboarding, VLAN and SSID templates, and alert notification profiles — so your team can manage day-to-day operations without networking expertise." },
  { q: "What is the difference between Netgear ProSAFE Smart Managed and the M4300 Fully Managed switches?", a: "Netgear's switch portfolio spans from simple unmanaged switches to enterprise-class fully managed Layer 3 devices — the key distinction for business deployments is between Smart Managed (Layer 2+ web-managed) and Fully Managed (Layer 3 CLI-managed). Netgear Smart Managed switches (GS308T, GS316T, GS748T series) are managed via a web browser interface and Netgear Insight — providing VLAN, QoS, link aggregation, and IGMP snooping configuration without requiring CLI expertise. They're ideal for SMBs needing basic network segmentation and management without a network engineer. Netgear M4300 Fully Managed switches are enterprise-class stackable Layer 3 devices with full CLI, SNMP v3, REST API, OSPF routing, VRRP redundancy, and 802.1X port security — offering capabilities comparable to traditional enterprise vendors (Cisco, HPE) at significantly lower price points. They're appropriate for organizations with network engineers who need full protocol support and performance at enterprise scale. ALG Data Guard helps customers select the right tier: Smart Managed for typical SMB environments, M4300 for organizations needing Layer 3 routing, high-availability stacking, or full protocol compliance." },
  { q: "Does Netgear business networking support high-density Wi-Fi 6E deployments?", a: "Yes — Netgear's WAX series access points deliver enterprise-class Wi-Fi 6E with tri-band 6 GHz support for high-density environments. The WAX630E is Netgear's flagship enterprise AP with tri-band 2.4 GHz + 5 GHz + 6 GHz operation — the 6 GHz band provides uncongested spectrum with no legacy device interference, significantly improving performance in dense environments. High-density deployment considerations: AP density planning based on user count and coverage requirements, channel allocation across the 6 GHz band (seven 160 MHz channels available), and multi-gig wired backhaul from M4250 or M4500 switches to support AP throughput. Netgear WAX APs support 802.1X RADIUS authentication for enterprise security, VLAN tagging for multiple SSID isolation, and band steering to optimize client distribution across frequency bands. ALG Data Guard conducts wireless site surveys using Ekahau or equivalent tools before deploying high-density Netgear wireless environments — ensuring adequate AP count, optimal placement, and channel plan before installation." },
  { q: "What is Netgear ProAV networking and which products are designed for AV over IP?", a: "Netgear ProAV is Netgear's product line specifically designed for AV over IP environments — where audio and video is transported over standard Ethernet infrastructure rather than dedicated coaxial or HDMI cabling. AV over IP protocols (Dante audio, NDI video, HDMI-over-IP, AVB) require network features that standard IT switches don't always handle well: low-latency forwarding, precise IGMP snooping for multicast, and PTP/IEEE 1588 time synchronization for audio sample-accurate distribution. Netgear's M4250 AV Line switches are purpose-built for AV environments — featuring an AV UI that configures IGMP snooping, multicast routing, and PTP with AV-specific terminology rather than networking jargon, making configuration accessible to AV engineers who aren't network specialists. The M4250 also includes QoS profiles optimized for Dante audio and NDI video traffic classes. ALG Data Guard provides ProAV networking design and deployment — including Dante domain configuration, NDI network design, and AVB switch configuration for broadcast studios, performance venues, houses of worship, and corporate AV environments." },
  { q: "How does ALG Data Guard support Netgear deployments and what ongoing services are available?", a: "ALG Data Guard provides complete Netgear lifecycle services: Pre-sales: network assessment, solution design (switching topology, wireless coverage planning, PoE power budgeting), and product selection across Netgear's business portfolio. Procurement: Netgear authorized reseller providing product sourcing with official warranty registration and direct manufacturer support access. Deployment: pre-configuration and staging of switches and APs before delivery, on-site installation, cable management, PoE device commissioning, and wireless RF survey validation confirming coverage meets specification. Post-deployment management: Netgear Insight organization management, firmware update coordination, network health monitoring, and SNMP-based monitoring integration for organizations with NOC visibility requirements. Support: hardware replacement coordination for in-warranty failures, configuration changes, VLAN modifications, and user addition support as your organization grows. Expansion planning: additional switch ports, AP additions for new coverage areas, and capacity planning as user count grows — including multi-gig upgrade planning for Wi-Fi 6E density requirements." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Netgear() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(40, 2200, metricsInView);
  const c2 = useCounter(300, 2000, metricsInView);
  const c3 = useCounter(90, 2200, metricsInView);
  const c4 = useCounter(25, 2000, metricsInView);

  return (
    <div className="ng-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={netgearLogo} alt="Netgear" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Business Networking Infrastructure Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Netgear</h1>
            <p className="pd-identity-desc">
              Netgear Business provides enterprise-class networking infrastructure — ProSAFE managed switching, Wi-Fi 6E wireless access points, ProAV networking for AV over IP environments, and Insight cloud management — at price points accessible for SMB and mid-market organizations that need enterprise performance without enterprise complexity or cost. From compact 8-port smart managed switches through fully managed 64-port stackable L3 enterprise switches to tri-band Wi-Fi 6E access points, the Netgear business portfolio covers every connectivity requirement. ALG Data Guard is an authorized Netgear Business reseller providing design, deployment, and managed services for the complete Netgear infrastructure portfolio.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="ng-hero">
        <div className="ng-hero__bg-circuit" aria-hidden="true" />
        <div className="ng-hero__glow-l" aria-hidden="true" />
        <div className="ng-hero__glow-r" aria-hidden="true" />
        <div className="ng-hero__inner">
          <div>
            <div className="ng-hero__eyebrow">Netgear Business Authorized Partner</div>
            <h2 className="ng-hero__title">
              Enterprise Networking.<br /><em>SMB Pricing.</em>
            </h2>
            <p className="ng-hero__body">
              Netgear's business networking portfolio delivers the switching performance, wireless coverage, and cloud management capabilities that enterprise organizations depend on — at price points that make professional networking accessible for growing businesses. ALG Data Guard designs, deploys, and manages Netgear environments so your team gets reliable connectivity without the complexity.
            </p>
            <div className="ng-hero__actions">
              <button className="ng-btn ng-btn--green" onClick={() => navigate("/contact")}>
                Design My Network <ArrowIcon />
              </button>
              <button className="ng-btn ng-btn--ghost-w" onClick={() => navigate("/contact")}>
                Network Assessment
              </button>
            </div>
            <div className="ng-hero__specs">
              <div><div className="ng-hero__spec-val">Wi-Fi 7</div><div className="ng-hero__spec-label">Latest Generation</div></div>
              <div><div className="ng-hero__spec-val">90W</div><div className="ng-hero__spec-label">PoE++ Per Port</div></div>
              <div><div className="ng-hero__spec-val">Insight</div><div className="ng-hero__spec-label">Cloud Managed</div></div>
            </div>
          </div>
          <div className="ng-hero__product">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Netgear business networking hardware"
              className="ng-hero__product-img"
            />
            <div className="ng-hero__product-badge">
              <div className="ng-hero__product-badge-val">M4300</div>
              <div className="ng-hero__product-badge-label">Enterprise Stack</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Product Cards ── */}
      <section className="ng-products">
        <div className="ng-shell">
          <div className="ng-products__head">
            <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Product Portfolio</div>
            <h2 className="ng-title">Enterprise Performance at Every Price Point</h2>
            <p className="ng-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Netgear's business product range covers switching, wireless, and AV networking — with Insight cloud management providing unified visibility across your complete network infrastructure.</p>
          </div>
          <div className="ng-product-grid">
            {productCards.map(p => (
              <div key={p.name} className="ng-product-card">
                <div className="ng-product-card__img-wrap">
                  <img src={p.img} alt={p.name} className="ng-product-card__img" />
                  <span className="ng-product-card__cat">{p.cat}</span>
                  {p.wifi7 && <span className="ng-product-card__wifi7">Wi-Fi 6E</span>}
                </div>
                <div className="ng-product-card__body">
                  <h3 className="ng-product-card__name">{p.name}</h3>
                  <p className="ng-product-card__desc">{p.desc}</p>
                  <div className="ng-product-card__specs">
                    {p.specs.map(s => <span key={s} className="ng-product-spec-pill">✓ {s}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Wi-Fi 6E Spotlight ── */}
      <section className="ng-wifi7">
        <div className="ng-shell">
          <div className="ng-wifi7__inner">
            <img
              src="https://images.unsplash.com/photo-1606868306217-dbf5046868d2?auto=format&fit=crop&w=900&q=80"
              alt="Netgear Wi-Fi 6E wireless deployment"
              className="ng-wifi7__img"
            />
            <div>
              <div className="ng-badge ng-badge--dark"><span className="ng-dot" /> Wi-Fi 6E Wireless</div>
              <h2 className="ng-wifi7__title">
                6 GHz. More Spectrum.<br /><em>Faster Everything.</em>
              </h2>
              <p className="ng-wifi7__body">
                Wi-Fi 6E adds the 6 GHz band — 1.2 GHz of clean, uncongested wireless spectrum available exclusively for Wi-Fi 6E devices. Where 2.4 GHz and 5 GHz bands are crowded with legacy devices, the 6 GHz band delivers consistently high throughput for high-density environments where every device is Wi-Fi 6E capable. Netgear WAX series 6E APs bring this capability to business environments at accessible price points.
              </p>
              <div className="ng-wifi7__advantages">
                {wifi7Advantages.map(a => (
                  <div key={a.val} className="ng-wifi7-adv">
                    <div className="ng-wifi7-adv__val">{a.val}</div>
                    <div className="ng-wifi7-adv__label">{a.label}</div>
                  </div>
                ))}
              </div>
              <p className="ng-wifi7__body">
                ALG Data Guard performs wireless site surveys before deploying Netgear Wi-Fi 6E environments — ensuring correct AP placement, channel planning across the 6 GHz band, and multi-gig backhaul sizing from M4250 or M4500 switches to support AP throughput without bottlenecks.
              </p>
              <button className="ng-btn ng-btn--green" onClick={() => navigate("/contact")}>
                Design My Wireless Network <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Managed Switches ── */}
      <section className="ng-switches">
        <div className="ng-shell">
          <div className="ng-switches__inner">
            <div>
              <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Switching Portfolio</div>
              <h2 className="ng-title">Every Switch Tier, Every Business Requirement</h2>
              <p className="ng-sub" style={{ marginBottom:"0" }}>
                Netgear's switching portfolio spans from plug-and-play simplicity to fully managed enterprise — with the right tier for every organization's management preferences, feature requirements, and budget constraints.
              </p>
              <div className="ng-switch-list">
                {switchItems.map(item => (
                  <div key={item.name} className="ng-switch-item">
                    <span className="ng-switch-item__icon">{item.icon}</span>
                    <div>
                      <div className="ng-switch-item__name">{item.name}</div>
                      <p className="ng-switch-item__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ng-btn ng-btn--outline" onClick={() => navigate("/contact")}>
                  Get Switch Recommendation <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80"
              alt="Netgear enterprise switching infrastructure"
              className="ng-switches__img"
            />
          </div>
        </div>
      </section>

      {/* ── Solutions ── */}
      <section className="ng-solutions">
        <div className="ng-shell">
          <div className="ng-solutions__head">
            <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Business Solutions</div>
            <h2 className="ng-title">Networking Solutions for Every Environment</h2>
            <p className="ng-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Netgear's business solutions address a wide range of connectivity challenges — from high-density enterprise wireless to AV over IP production environments requiring specialized network performance.</p>
          </div>
          <div className="ng-solutions__grid">
            {solutions.map(sol => (
              <div key={sol.title} className="ng-solution-card">
                <div className="ng-solution-card__accent" />
                <span className="ng-solution-card__icon">{sol.icon}</span>
                <h3 className="ng-solution-card__title">{sol.title}</h3>
                <p className="ng-solution-card__desc">{sol.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Remote/Hybrid Management ── */}
      <section className="ng-remote">
        <div className="ng-shell">
          <div className="ng-remote__inner">
            <img
              src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=80"
              alt="Netgear Insight cloud management"
              className="ng-remote__img"
            />
            <div>
              <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Cloud Management</div>
              <h2 className="ng-title">Manage Your Network from Anywhere — Anytime</h2>
              <p className="ng-sub" style={{ marginBottom:"0" }}>
                Netgear Insight provides cloud-based network management that doesn't require being on-site — configure switches, manage wireless, troubleshoot devices, and receive alerts from your smartphone wherever you are. For organizations with multiple locations, Insight provides unified visibility across all sites from a single account.
              </p>
              <div className="ng-remote__checklist">
                {remoteChecklist.map(item => (
                  <div key={item.title} className="ng-check">
                    <div className="ng-check__icon">{item.icon}</div>
                    <div>
                      <div className="ng-check__title">{item.title}</div>
                      <p className="ng-check__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ng-btn ng-btn--outline" onClick={() => navigate("/contact")}>
                  Set Up Insight Management <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Process ── */}
      <section className="ng-deploy">
        <div className="ng-shell">
          <div className="ng-deploy__head">
            <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Deployment Process</div>
            <h2 className="ng-title">From Design to Live Network — Without Surprises</h2>
            <p className="ng-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's structured deployment process ensures your Netgear network is configured correctly, tested thoroughly, and handed over fully documented — with no surprises on installation day.</p>
          </div>
          <div className="ng-deploy__steps">
            {deploySteps.map(step => (
              <div key={step.num} className="ng-deploy-step">
                <div className="ng-deploy-step__num">{step.num}</div>
                <h3 className="ng-deploy-step__title">{step.title}</h3>
                <p className="ng-deploy-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SMB Tiers ── */}
      <section className="ng-smb">
        <div className="ng-shell">
          <div style={{ textAlign:"center", marginBottom:"3rem" }}>
            <div className="ng-badge ng-badge--dark"><span className="ng-dot" /> Service Tiers</div>
            <h2 className="ng-title" style={{ color:"#fff" }}>Right-Sized Networking for Every Organization</h2>
            <p className="ng-sub" style={{ color:"rgba(255,255,255,0.65)", margin:"0 auto", maxWidth:"580px" }}>Whether you're a growing SMB needing cloud-managed simplicity or an enterprise requiring full L3 protocol support, ALG Data Guard delivers the right Netgear solution tier for your specific requirements.</p>
          </div>
          <div className="ng-smb__inner">
            {smbPanels.map(panel => (
              <div key={panel.tier} className={`ng-smb-panel ${panel.tier === "Pro Managed" ? "ng-smb-panel--pro" : "ng-smb-panel--insight"}`}>
                <div className="ng-smb-panel__tier">{panel.tier}</div>
                <h3 className="ng-smb-panel__title">{panel.title}</h3>
                <p className="ng-smb-panel__sub">{panel.sub}</p>
                <ul className="ng-smb-panel__features">
                  {panel.features.map(f => <li key={f}>{f}</li>)}
                </ul>
                <div style={{ marginTop:"2rem" }}>
                  <button className="ng-btn ng-btn--outline" style={{ borderColor:"rgba(255,255,255,0.3)", color:"#fff", borderRadius:"100px", padding:"11px 22px" }} onClick={() => navigate("/contact")}>
                    Get This Tier <ArrowIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="ng-metrics" ref={metricsRef}>
        <div className="ng-shell">
          <div className="ng-metrics__inner">
            <div><div className="ng-metric__val">{c1}+</div><div className="ng-metric__label">Years of Business Networking</div></div>
            <div><div className="ng-metric__val">{c2}+</div><div className="ng-metric__label">Clients Per Wi-Fi 6E AP</div></div>
            <div><div className="ng-metric__val">{c3}W</div><div className="ng-metric__label">PoE++ Per Port Maximum</div></div>
            <div><div className="ng-metric__val">{c4}yr</div><div className="ng-metric__label">Lifetime Hardware Warranty</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="ng-certs">
        <div className="ng-shell">
          <div className="ng-certs__head">
            <div className="ng-badge ng-badge--green"><span className="ng-dot" /> Partnership</div>
            <h2 className="ng-title">Authorized Netgear Business Partner</h2>
            <p className="ng-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>ALG Data Guard's authorized Netgear partnership provides direct access to the complete product portfolio, manufacturer support, and partner pricing for business networking projects.</p>
          </div>
          <div className="ng-certs__grid">
            {certCards.map(cert => (
              <div key={cert.name} className="ng-cert-card">
                <span className="ng-cert-card__icon">{cert.icon}</span>
                <h3 className="ng-cert-card__name">{cert.name}</h3>
                <p className="ng-cert-card__desc">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ng-faq">
        <div className="ng-shell">
          <div className="ng-faq__head">
            <div className="ng-badge ng-badge--green" style={{ margin:"0 auto 1rem" }}><span className="ng-dot" /> FAQs</div>
            <h2 className="ng-title" style={{ color:"var(--ng-text)" }}>Netgear Business Questions, Answered</h2>
            <p className="ng-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers from ALG Data Guard's Netgear-certified engineers on Insight management, Wi-Fi 6E deployment, managed switching, and ProAV networking.</p>
          </div>
          <div className="ng-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ng-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ng-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ng-faq-item__q">{faq.q}</span>
                  <span className="ng-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ng-faq-item__answer"><div className="ng-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ng-cta">
        <div className="ng-cta__inner">
          <div className="ng-badge ng-badge--dark" style={{ margin:"0 auto 1.2rem" }}><span className="ng-dot" /> Get Started</div>
          <h2 className="ng-cta__title">
            Build Reliable Business Networking with Netgear & ALG Data Guard.
          </h2>
          <p className="ng-cta__sub">Whether you're deploying your first managed switching environment, upgrading to Wi-Fi 6E, building an AV over IP production network, or expanding to multiple locations — ALG Data Guard's Netgear-certified engineers design and deliver networking solutions that work reliably from day one.</p>
          <div className="ng-cta__actions">
            <button className="ng-btn ng-btn--green" onClick={() => navigate("/contact")}>
              Design My Network <ArrowIcon />
            </button>
            <button className="ng-btn ng-btn--ghost-w" onClick={() => navigate("/contact")}>
              Get a Product Quote
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
